import * as XLSX from 'xlsx';

/* Raw decimal hours */
const formatHours = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${Number(n.toFixed(2))} h`;
};

export const OT_EXPORT_COLUMNS = [
    { key: 'employeeName',           label: 'Employee',           width: 26 },
    { key: 'date',                   label: 'Date',               width: 14 },
    { key: 'shiftType',              label: 'Shift Type',         width: 16 },
    { key: 'otTypeLabel',            label: 'OT Type',            width: 18 },
    { key: 'inTime',                 label: 'In Time',            width: 12 },
    { key: 'outTime',                label: 'Out Time',           width: 12 },
    { key: 'otHours',                label: 'OT Hours',           width: 14, time: true },
    { key: 'HOT_OTHOURS_RATE',       label: 'OT Hours Rate',      width: 16, numeric: true },
    { key: 'HOT_ADJUSTED_OT_HOURS',  label: 'Adjusted OT Hours',  width: 20, time: true },
    { key: 'otRate',                 label: 'OT Rate',            width: 14, numeric: true },
    { key: 'otAmount',               label: 'OT Amount',          width: 16, numeric: true },
    {
        key: 'isApproved',
        label: 'Status',
        width: 14,
        format: (row) => {
            if (
                row.isApproved === true ||
                row.isApproved === 1 ||
                row.isApproved === 'A'
            ) {
                return 'Approved';
            }

            if (
                row.isApproved === false ||
                row.isApproved === 0 ||
                row.isApproved === 'P'
            ) {
                return 'Pending';
            }

            return row.status ?? row.isApproved ?? '';
        },
    },
];

const pad2 = (value) => String(value).padStart(2, '0');

const getTimestamp = (date = new Date()) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ` +
    `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const round2 = (value) =>
    Math.round((value + Number.EPSILON) * 100) / 100;

export function exportOTRecordsToExcel({ rows = [], filters = {} } = {}) {
    const safeRows = Array.isArray(rows) ? rows : [];

    const activeFilters =
        Object.entries(filters)
            .filter(([, value]) =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                value !== 'All' &&
                value !== 'None'
            )
            .map(([key, value]) => `${key}: ${value}`)
            .join('   |   ') || 'None';

    const headerRows = [
        ['OVERTIME DETAILS REPORT'],
        [`Filters: ${activeFilters}`],
        [`Generated: ${getTimestamp()}`],
        [`Records: ${safeRows.length}`],
        [],
    ];

    const columnHeaders = OT_EXPORT_COLUMNS.map((column) => column.label);

    const bodyRows = safeRows.map((row) =>
        OT_EXPORT_COLUMNS.map((column) => {
            // Custom formatter (Status)
            if (column.format) return column.format(row);

            const value = row[column.key];

            // Duration columns → "7.77 h"
            if (column.time) return formatHours(value);

            // Numeric (rates, amounts)
            if (column.numeric) return toNumber(value);

            // Plain text
            return value === null || value === undefined ? '' : value;
        })
    );

    /* Totals row */
    const totalsRow = OT_EXPORT_COLUMNS.map((column) => {
        if (column.key === 'employeeName') {
            return `TOTAL — ${safeRows.length} record${safeRows.length === 1 ? '' : 's'}`;
        }

        // Duration columns → sum raw decimals, then "X.XX h"
        if (column.time) {
            const total = safeRows.reduce(
                (sum, row) => sum + toNumber(row[column.key]),
                0
            );
            return formatHours(total);
        }

        // OT Amount
        if (column.key === 'otAmount') {
            return round2(
                safeRows.reduce(
                    (sum, row) => sum + toNumber(row[column.key]),
                    0
                )
            );
        }

        return '';
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
        ...headerRows,
        columnHeaders,
        ...bodyRows,
        [],
        totalsRow,
    ]);

    worksheet['!cols'] = OT_EXPORT_COLUMNS.map((column) => ({
        wch: column.width || 12,
    }));

    const lastColumn = OT_EXPORT_COLUMNS.length - 1;

    worksheet['!merges'] = headerRows
        .slice(0, 4)
        .map((_, index) => ({
            s: { r: index, c: 0 },
            e: { r: index, c: lastColumn },
        }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, 'OT Details');

    const date = new Date();

    const filename =
        `OT_Details_${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}.xlsx`;

    XLSX.writeFile(workbook, filename);

    return filename;
}